import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { Observable, of } from 'rxjs';
import { filter } from 'rxjs/operators';
import { map, switchMap } from 'rxjs/operators';
import { JobRequestSummaryDto } from '@models/job-request-summary.interface';
import { MudCheckTileComponent } from '../mud-check-tile/mud-check-tile.component';
import { JobRequestTileComponent } from '../job-request-tile/job-request-tile.component';
import { JobRequestsService } from '../../../services/job-requests.service';
import { DesignService } from '../../../services/design.service';
import { PilotTestDialogComponent, PilotTestDialogData } from '../pilot-test/pilot-test-dialog/pilot-test-dialog.component';
import { WorksheetService } from '../../../services/worksheet.service';
import { MudCheckWizardComponent } from '../mud-check-wizard/mud-check-wizard.component';
import { ShippingInformationDialogComponent, ShippingInformationDialogData } from '../shipping-information/shipping-information-dialog.component';
import {
  IterationItem,
  IterationsDialogComponent,
  IterationsDialogData,
  IterationsDialogResult
} from '../../../shared/components/iterations-dialog/iterations-dialog.component';


@Component({
  selector: 'app-job-tests',
  standalone: true,
  imports: [CommonModule, MudCheckTileComponent, JobRequestTileComponent, IterationsDialogComponent],
  templateUrl: './job-tests.component.html',
  styleUrl: './job-tests.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class JobTestsComponent {
  @Input() mudCheckRequests: JobRequestSummaryDto[] = [];
  @Input() requests: JobRequestSummaryDto[] = [];
  @Input() jobId!: number;

  @Output() changed = new EventEmitter<void>();

  private readonly jobRequestsService = inject(JobRequestsService);
  private readonly designService = inject(DesignService);
  private readonly worksheetService = inject(WorksheetService);
  private readonly router = inject(Router);
  private readonly dialog = inject(MatDialog);

  // Run Mud Check is only available when no mud check request exists yet.
  protected get canRunMudCheck(): boolean {
    return this.mudCheckRequests.length === 0;
  }

  // As Received requires at least one mud check design with a pycnometer.
  private get hasMudCheckPycnometer(): boolean {
    return this.mudCheckRequests.some(r => r.hasPycnometer);
  }

  private get hasMudCheckCutbackPrerequisites(): boolean {
    return this.mudCheckRequests.some(r => r.hasCutbackPrerequisites);
  }

  protected get canRunCutback(): boolean {
    return this.hasMudCheckCutbackPrerequisites;
  }

  // Comparison test (PilotTest) is always available.
  protected get canRunComparison(): boolean {
    return true;
  }

  protected get canRunAsReceived(): boolean {
    return this.hasMudCheckPycnometer;
  }

  protected onAddMudCheck(): void {
    this.dialog.open(MudCheckWizardComponent, {
      data: { jobId: this.jobId },
      width: '1000px',
      maxWidth: 'none',
      maxHeight: '100vh',
    }).afterClosed().pipe(filter(result => !!result)).subscribe(() => this.changed.emit());
  }

  protected onAddPilot(): void {
    this.openPilotTestDialog({ jobId: this.jobId, requestType: 'pilot' });
  }

  protected onAddCutback(): void {
    this.openPilotTestDialog({ jobId: this.jobId, requestType: 'cutback' });
  }

  protected onAddMaterial(): void { /* TODO: Open add Material As Received request */ }

  private openPilotTestDialog(data: PilotTestDialogData): void {
    const dialogRef = this.dialog.open<PilotTestDialogComponent, PilotTestDialogData, boolean>(
      PilotTestDialogComponent,
      {
        data,
        width: data.requestType === 'cutback' ? '640px' : '700px',
        maxWidth: '95vw',
        maxHeight: '95vh',
        disableClose: true,
        panelClass: 'pilot-test-dialog-panel'
      }
    );

    dialogRef.afterClosed().subscribe(saved => {
      if (saved) this.changed.emit();
    });
  }

  protected onJobRequestView(jobRequest: JobRequestSummaryDto): void {
    this.router.navigate(['/jobs', this.jobId, 'requests', jobRequest.id]);
  }

  protected onOpenJobRequestReport(jobRequest: JobRequestSummaryDto): void {
    const requiresIterationSelection = jobRequest.isDensityChange
      || (jobRequest.jobRequestTypeName ?? '').trim().toLowerCase() === 'pilottest';
    if (!requiresIterationSelection) {
      this.router.navigate(['/jobs', this.jobId, 'requests', jobRequest.id, 'report']);
      return;
    }

    this.designService.getIterations(jobRequest.id).subscribe({
      next: (iterations) => {
        const designs = iterations?.designs ?? [];
        if (!designs || designs.length === 0) {
          alert('No iterations are available for this report.');
          return;
        }

        const dialogRef = this.dialog.open<IterationsDialogComponent, IterationsDialogData, IterationItem | null>(
          IterationsDialogComponent,
          {
            data: {
              title: 'Select Iteration',
              primaryText: 'Choose the iteration to include in the report.',
              iterations: designs.map(d => ({ id: d.id, label: d.name ?? `Design ${d.id}` })),
              singleSelect: false,
              confirmText: 'Generate Report',
              cancelText: 'Cancel'
            },
            width: '560px',
            maxWidth: '95vw',
            disableClose: true
          }
        );

        dialogRef.afterClosed().subscribe(result => {
          if (!result) return;

          const ids = Array.isArray(result)
            ? (result as IterationItem[]).map(i => String(i.id))
            : [String((result as IterationItem).id)];

          this.router.navigate(
            ['/jobs', this.jobId, 'requests', jobRequest.id, 'report'],
            { queryParams: { designid: ids } }
          );
        });
      },
      error: () => {
        alert('Failed to load iterations');
      }
    });
  }

  protected onOpenJobRequestColumnReport(jobRequest: JobRequestSummaryDto): void {
    this.designService.getIterations(jobRequest.id).subscribe({
      next: iterations => {
        const designs = iterations?.designs ?? [];
        if (designs.length === 0) {
          alert('No iterations are available for this Pilot Test column report.');
          return;
        }

        this.dialog.open<IterationsDialogComponent, IterationsDialogData, IterationsDialogResult | null>(
          IterationsDialogComponent,
          {
            data: {
              title: 'Pilot Test Column Report',
              primaryText: 'Choose the iterations to include in the report.',
              iterations: designs.map(design => ({
                id: design.id,
                label: design.name ?? `Design ${design.id}`
              })),
              singleSelect: false,
              showFormulationOption: true,
              confirmText: 'Generate Report',
              cancelText: 'Cancel'
            },
            width: '560px',
            maxWidth: '95vw',
            disableClose: true
          }
        ).afterClosed().subscribe(result => {
          if (!result) return;

          const selectedIterations = result.iterations;

          this.router.navigate(
            ['/jobs', this.jobId, 'requests', jobRequest.id, 'column-report'],
            {
              queryParams: {
                designid: (Array.isArray(selectedIterations)
                  ? selectedIterations
                  : [selectedIterations]).map(iteration => String(iteration.id)),
                showFormulation: result.showFormulation
              }
            }
          );
        });
      },
      error: () => alert('Failed to load Pilot Test iterations.')
    });
  }

  protected onOpenJobRequestWorksheet(jobRequest: JobRequestSummaryDto): void {
    const requiresIterationSelection = jobRequest.isDensityChange
      || (jobRequest.jobRequestTypeName ?? '').trim().toLowerCase() === 'pilottest';
    if (!requiresIterationSelection) {
      const url = this.router.serializeUrl(
        this.router.createUrlTree(['/jobs', this.jobId, 'worksheets', 'request', jobRequest.id])
      );
      window.open(url, '_blank');
      return;
    }
 
    this.designService.getIterations(jobRequest.id).subscribe({
      next: (iterations) => {
        const designs = iterations?.designs ?? [];
        if (!designs || designs.length === 0) {          
          alert('No iterations are available for this Pilot Test.');
          return;
        }

        const dialogRef = this.dialog.open<IterationsDialogComponent, IterationsDialogData, IterationItem | null>(
          IterationsDialogComponent,
          {
            data: {
              title: 'Select Iteration',
              primaryText: 'Choose the iteration to include in the worksheet.',
              iterations: designs.map(d => ({ id: d.id, label: d.name ?? `Design ${d.id}` })),
              singleSelect: false,
              confirmText: 'Generate Worksheet',
              cancelText: 'Cancel'
            },
            width: '560px',
            maxWidth: '95vw',
            disableClose: true
          }
        );

        dialogRef.afterClosed().subscribe(result => {
          if (!result) return;
          
          const printWindow = window.open('', '_blank');
          const ids = Array.isArray(result)
            ? (result as IterationItem[]).map(i => String(i.id))
            : [String((result as IterationItem).id)];

          const url = this.router.serializeUrl(
            this.router.createUrlTree([
              '/jobs', this.jobId, 'worksheets', 'request', jobRequest.id
            ], { queryParams: { designid: ids } })
          );

          if (printWindow) {
            printWindow.location.href = url;
          } else {
            window.open(url, '_blank');
          }
        });
      },
      error: () => {
        alert('Failed to load iterations');
      }
    });
  }

  protected onOpenMudCheckWorksheet(jobRequest: JobRequestSummaryDto): void {
    // Open the tab synchronously within the click handler so the browser
    // popup blocker does not cancel it later (the worksheet lookup is async
    // and would otherwise open the tab outside a user gesture).
    const printWindow = window.open('', '_blank');
    this.worksheetService.getJobWorksheets(this.jobId).subscribe({
      next: (worksheets) => {
        const worksheet = worksheets.find(w => w.jobRequestId === jobRequest.id);
        if (worksheet) {
          const url = `/jobs/${this.jobId}/worksheets/${worksheet.testGroupId}`;
          if (printWindow) {
            printWindow.location.href = url;
          } else {
            window.open(url, '_blank');
          }
        } else {
          printWindow?.close();
          alert('No worksheet is available for this job request yet.');
        }
      },
      error: () => {
        printWindow?.close();
        alert('Failed to open worksheet');
      }
    });
  }

  protected onDeleteJobRequest(jobRequest: JobRequestSummaryDto): void {
    if (!confirm(`Delete job request ${jobRequest.name || jobRequest.id}?`)) return;
    this.jobRequestsService.deleteJobRequest(jobRequest.id).subscribe({
      next: () => this.changed.emit(),
      error: () => alert('Failed to delete job request')
    });
  }

  protected onRemoveMudCheckMaterial(jobRequest: JobRequestSummaryDto): void {
    if (!jobRequest.firstSingleMaterialDesign?.materialId) return;
    if (!confirm(`Remove the selected material from mud check ${jobRequest.name || jobRequest.id}?`)) return;

    this.designService.removeMudCheckMaterial(jobRequest.id).subscribe({
      next: () => this.changed.emit(),
      error: () => alert('Failed to remove selected material')
    });
  }

  protected onEditJobRequest(jobRequest: JobRequestSummaryDto): void {
    if (jobRequest.isDensityChange) {
      this.openPilotTestDialog({ jobId: this.jobId, jobRequestId: jobRequest.id, requestType: 'cutback' });
      return;
    }

    if (jobRequest.jobRequestTypeName === 'Pilot Test') {
      this.openPilotTestDialog({ jobId: this.jobId, jobRequestId: jobRequest.id, requestType: 'pilot' });
      return;
    }
    // TODO: Open EditJobRequestView dialog for other job request types
    alert('Edit job request not yet implemented.');
  }

  protected onShipJobRequest(jobRequest: JobRequestSummaryDto): void {
    this.openShippingInformationDialog(jobRequest).subscribe({
      next: (saved) => {
        if (saved) this.changed.emit();
      },
      error: () => alert('Failed to record shipment information')
    });
  }

  protected onSendReportJobRequest(event: { jobRequest: JobRequestSummaryDto; isReportSent: boolean }): void {
    this.jobRequestsService.markReportSent(event.jobRequest.id, event.isReportSent).subscribe({
      next: () => this.changed.emit(),
      error: () => alert('Failed to update report sent status')
    });
  }

  protected onLockJobRequest(jobRequest: JobRequestSummaryDto): void {
    if (!confirm(`Mark job request "${jobRequest.name || jobRequest.id}" as completed (lock)?`)) return;

    this.runLockWorkflow(jobRequest).pipe(
      switchMap(shouldLock => shouldLock ? this.jobRequestsService.lockJobRequest(jobRequest.id).pipe(map(() => true)) : of(false))
    ).subscribe({
      next: (locked) => {
        if (locked) this.changed.emit();
      },
      error: () => alert('Failed to lock job request')
    });
  }

  protected onUnlockJobRequest(jobRequest: JobRequestSummaryDto): void {
    if (!confirm(`Unlock job request "${jobRequest.name || jobRequest.id}"?`)) return;
    this.jobRequestsService.unlockJobRequest(jobRequest.id).subscribe({
      next: () => this.changed.emit(),
      error: () => alert('Failed to unlock job request')
    });
  }

  private runLockWorkflow(jobRequest: JobRequestSummaryDto): Observable<boolean> {
    const isMudCheck = (jobRequest.jobRequestTypeName ?? '').trim().toLowerCase() === 'mud check';
    if (isMudCheck || jobRequest.hasShipmentRecords) {
      return of(true);
    }

    return this.openShippingInformationDialog(jobRequest);
  }

  private openShippingInformationDialog(jobRequest: JobRequestSummaryDto): Observable<boolean> {
    const dialogRef = this.dialog.open<ShippingInformationDialogComponent, ShippingInformationDialogData, boolean>(
      ShippingInformationDialogComponent,
      {
        data: {
          jobRequestId: jobRequest.id,
          jobRequestName: jobRequest.name ?? `#${jobRequest.id}`,
        },
        width: '720px',
        maxWidth: '95vw',
        disableClose: true,
      }
    );

    return dialogRef.afterClosed().pipe(map(result => result === true));
  }
}
